/** AdMockOverlay — Reward Ad の擬似実装(Phase A、実 SDK なし)。
 *
 *  Reference: docs/PM_UPGRADE_PLAN.md §3 (Reward Ad Mock S1-2)
 *  - 3 秒程度のフルスクリーンモーダル + カウントダウン
 *  - "Watching ad..." → 完了で Promise<true> resolve
 *  - 任意のキャンセル(右上 ✕)で Promise<false> resolve
 *  - GDD §10 / CLAUDE.md §4 非ゴール:Reward Ad のみ採用、Banner / Interstitial 不採用
 *
 *  使い方:
 *    const ad = new AdMockOverlay(document.body);
 *    const success = await ad.show({ rewardLabel: '+1 ❤️' });
 *    if (success) heartsStore.addOne();
 */

export interface AdMockOptions {
  /** 報酬の表示ラベル(例: "+1 ❤️" / "Continue" / "+30 🪙") */
  readonly rewardLabel: string;
  /** カウントダウン秒(デフォルト 3 秒) */
  readonly durationMs?: number;
}

export class AdMockOverlay {
  private readonly overlay: HTMLDivElement;
  private resolveFn: ((success: boolean) => void) | null = null;
  private rafId: number | null = null;

  constructor(root: HTMLElement = document.body) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'ad-mock-overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="ad-mock-card">
        <div class="ad-mock-tag">SPONSORED</div>
        <button class="ad-mock-skip" type="button" data-ad-skip aria-label="Skip">✕</button>
        <div class="ad-mock-content">
          <div class="ad-mock-anim" aria-hidden="true">📺</div>
          <div class="ad-mock-title">Watching ad...</div>
          <div class="ad-mock-sub">Reward: <strong data-ad-reward>—</strong></div>
        </div>
        <div class="ad-mock-progress">
          <div class="ad-mock-progress-fill" data-ad-fill></div>
        </div>
        <div class="ad-mock-count" data-ad-count>3</div>
        <div class="ad-mock-note">(Mock — no real ad is shown)</div>
      </div>
    `;
    root.appendChild(this.overlay);

    this.overlay
      .querySelector<HTMLButtonElement>('[data-ad-skip]')
      ?.addEventListener('click', () => this.finish(false));
  }

  show(options: AdMockOptions): Promise<boolean> {
    // 既存呼び出しが残ってたら強制 fail
    if (this.resolveFn) {
      this.resolveFn(false);
      this.resolveFn = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    const duration = Math.max(500, options.durationMs ?? 3000);
    const rewardEl = this.overlay.querySelector<HTMLElement>('[data-ad-reward]');
    const fillEl = this.overlay.querySelector<HTMLElement>('[data-ad-fill]');
    const countEl = this.overlay.querySelector<HTMLElement>('[data-ad-count]');
    if (rewardEl) rewardEl.textContent = options.rewardLabel;
    if (fillEl) fillEl.style.width = '0%';
    if (countEl) countEl.textContent = String(Math.ceil(duration / 1000));

    this.overlay.hidden = false;
    this.overlay.setAttribute('aria-hidden', 'false');
    this.overlay.classList.add('is-open');

    return new Promise<boolean>((resolve) => {
      this.resolveFn = resolve;
      const startTs = performance.now();
      const tick = (): void => {
        const elapsed = performance.now() - startTs;
        const t = Math.min(1, elapsed / duration);
        if (fillEl) fillEl.style.width = `${(t * 100).toFixed(1)}%`;
        if (countEl) countEl.textContent = String(Math.ceil((duration - elapsed) / 1000));
        if (t >= 1) {
          this.finish(true);
        } else {
          this.rafId = requestAnimationFrame(tick);
        }
      };
      this.rafId = requestAnimationFrame(tick);
    });
  }

  private finish(success: boolean): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      this.overlay.hidden = true;
    }, 220);
    const r = this.resolveFn;
    this.resolveFn = null;
    r?.(success);
  }
}
